/**
 * Reusable bookmark toggle button.
 *
 * Pass the item's identity (item_type + item_id) and optional cache fields
 * (title/subtitle/url) so the backend can skip hydration. Uses react-query for
 * cache management and optimistic updates.
 */
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import {
  bookmarksAPI,
  BookmarkItemType,
  BookmarkCheckResponse,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useBookmarkBatch } from './BookmarkBatchProvider';

interface BookmarkButtonProps {
  itemType: BookmarkItemType;
  itemId: string;
  title?: string;
  subtitle?: string;
  url?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'icon' | 'button';
  className?: string;
}

const SIZE_PX = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-6 h-6' };
const BUTTON_PAD = { sm: 'p-1.5', md: 'p-2', lg: 'p-2.5' };

export default function BookmarkButton({
  itemType,
  itemId,
  title,
  subtitle,
  url,
  size = 'md',
  variant = 'icon',
  className = '',
}: BookmarkButtonProps): ReactElement {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const batch = useBookmarkBatch();
  const [showHint, setShowHint] = useState(false);
  // Local override after a mutation, so the UI reflects the change immediately
  // without waiting for the batch query to refetch.
  const [localOverride, setLocalOverride] = useState<BookmarkCheckResponse | null>(null);

  const queryKey = ['bookmark-check', itemType, itemId];

  // When inside a BookmarkBatchProvider, the batch query handles fetching for
  // all cards on the page in a single round-trip. Skip the per-button fetch.
  const inBatchMode = !!batch?.inBatch;

  const { data, isLoading: checkLoading } = useQuery<BookmarkCheckResponse>({
    queryKey,
    queryFn: async () => {
      const res = await bookmarksAPI.check(itemType, itemId);
      return res.data;
    },
    enabled: !inBatchMode && isAuthenticated && !!itemId,
    staleTime: 60_000,
  });

  const batchStatus = batch?.getStatus(itemType, itemId);
  const effective: BookmarkCheckResponse =
    localOverride ??
    (batchStatus ?? data ?? { bookmarked: false, id: null });
  const bookmarked = !!effective.bookmarked;

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await bookmarksAPI.create({
        item_type: itemType,
        item_id: itemId,
        title,
        subtitle,
        url,
      });
      return res.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<BookmarkCheckResponse>(queryKey);
      const optimistic: BookmarkCheckResponse = { bookmarked: true, id: previous?.id ?? null };
      queryClient.setQueryData(queryKey, optimistic);
      setLocalOverride(optimistic);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
      setLocalOverride(null);
    },
    onSuccess: (created) => {
      const next: BookmarkCheckResponse = { bookmarked: true, id: created.id };
      queryClient.setQueryData(queryKey, next);
      setLocalOverride(next);
      batch?.setStatus(itemType, itemId, { bookmarked: true, id: created.id });
      queryClient.invalidateQueries({ queryKey: ['bookmarks-list'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await bookmarksAPI.deleteByItem(itemType, itemId);
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<BookmarkCheckResponse>(queryKey);
      const optimistic: BookmarkCheckResponse = { bookmarked: false, id: null };
      queryClient.setQueryData(queryKey, optimistic);
      setLocalOverride(optimistic);
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous);
      setLocalOverride(null);
    },
    onSuccess: () => {
      const next: BookmarkCheckResponse = { bookmarked: false, id: null };
      queryClient.setQueryData(queryKey, next);
      setLocalOverride(next);
      batch?.setStatus(itemType, itemId, { bookmarked: false, id: null });
      queryClient.invalidateQueries({ queryKey: ['bookmarks-list'] });
    },
  });

  const isLoading = checkLoading || createMutation.isPending || deleteMutation.isPending;

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      setShowHint(true);
      setTimeout(() => setShowHint(false), 2200);
      return;
    }
    if (bookmarked) {
      deleteMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  if (variant === 'button') {
    return (
      <div className="relative inline-block">
        <button
          onClick={handleClick}
          disabled={isLoading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
            bookmarked
              ? 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
              : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:text-gray-800'
          } disabled:opacity-60 ${className}`}
          aria-pressed={bookmarked}
          title={bookmarked ? 'Remove bookmark' : 'Save'}
        >
          {isLoading ? (
            <Loader2 className={`${SIZE_PX[size]} animate-spin`} />
          ) : bookmarked ? (
            <BookmarkCheck className={SIZE_PX[size]} fill="currentColor" />
          ) : (
            <Bookmark className={SIZE_PX[size]} />
          )}
          <span>{bookmarked ? 'Saved' : 'Save'}</span>
        </button>
        {showHint && (
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 px-2 py-1 text-xs bg-gray-800 text-white rounded shadow whitespace-nowrap z-10">
            Sign in to bookmark
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/login');
              }}
              className="ml-2 underline"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`${BUTTON_PAD[size]} rounded-lg transition-all ${
          bookmarked
            ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50'
            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
        } disabled:opacity-60 ${className}`}
        aria-pressed={bookmarked}
        aria-label={bookmarked ? 'Remove bookmark' : 'Save bookmark'}
        title={bookmarked ? 'Remove bookmark' : 'Save'}
      >
        {isLoading ? (
          <Loader2 className={`${SIZE_PX[size]} animate-spin`} />
        ) : bookmarked ? (
          <BookmarkCheck className={SIZE_PX[size]} fill="currentColor" />
        ) : (
          <Bookmark className={SIZE_PX[size]} />
        )}
      </button>
      {showHint && (
        <div className="absolute top-full right-0 mt-1 px-2 py-1 text-xs bg-gray-800 text-white rounded shadow whitespace-nowrap z-10">
          Sign in to bookmark
        </div>
      )}
    </div>
  );
}
